import { differenceInDays, differenceInHours, differenceInMinutes } from 'date-fns';
import type { IntlShape } from 'react-intl';

export type RelativeTimeValue = {
	type: 'relative' | 'absolute';
	value: string;
};

type GetRelativeTimeAgoParams = {
	lastRelevantDate: string;
	intl: IntlShape;
};

/**
 * Formats a date as either relative time (e.g., "2d", "1h") or absolute date (e.g., "Aug 8, 2018").
 * Returns an object with both the formatted value and type to allow proper i18n handling.
 *
 * For dates within 7 days: returns relative format using Intl.RelativeTimeFormat (e.g., "2d ago")
 * For dates older than 7 days: returns absolute date format using intl.formatDate (e.g., "Aug 8, 2018")
 *
 * @param lastRelevantDate - The date to format as a string (milliseconds since epoch as string, e.g., "1234567890")
 * @param intl - The intl object from react-intl for locale-aware formatting
 * @returns Object with type and formatted value string (just the time/date, without "ago" or "by")
 */
export const getRelativeTimeAgo = ({
	lastRelevantDate,
	intl,
}: GetRelativeTimeAgoParams): RelativeTimeValue => {
	// Convert string (milliseconds) to Date
	const date = new Date(parseInt(lastRelevantDate, 10));
	const now = new Date();
	const diffInMinutes = differenceInMinutes(now, date);
	const diffInHours = differenceInHours(now, date);
	const diffInDays = differenceInDays(now, date);

	// Create a RelativeTimeFormat instance for the intl locale
	// oxlint-disable-next-line compat/compat
	const rtf = new Intl.RelativeTimeFormat((intl as any).locale || 'en-US', {
		numeric: 'always',
		style: 'narrow',
	});

	if (diffInMinutes < 1) {
		return { type: 'relative', value: 'now' };
	}

	if (diffInMinutes < 60) {
		// Returns something like "5m ago" with numeric: 'auto'
		return { type: 'relative', value: rtf.format(-diffInMinutes, 'minute') };
	}

	if (diffInHours < 24) {
		// Returns something like "3h ago" with numeric: 'auto'
		return { type: 'relative', value: rtf.format(-diffInHours, 'hour') };
	}

	if (diffInDays <= 7) {
		// Returns something like "2d ago" with numeric: 'auto'
		return { type: 'relative', value: rtf.format(-diffInDays, 'day') };
	}

	// For >7 days, use truncated date format (Aug 8, 2018)
	const formattedDate = intl.formatDate(date, {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});

	return { type: 'absolute', value: formattedDate };
};
